/**
 * Panel „Tasks" der Uebersicht - woertliche Portierung von `index.html:14776-14803`
 * (samt des Helfers `taskFilter`, `index.html:14662`).
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. `overdue` verlangt `!task.done && !!due && due < today` - eine Aufgabe OHNE Termin ist nie
 *    ueberfaellig, egal wie alt sie ist.
 * 2. `mine` vergleicht `normSearch(task.assignee)` mit dem normalisierten Anzeigenamen des
 *    angemeldeten Nutzers. Ohne angemeldeten Nutzer (`currentAssignee === ""`) ist `mine` fuer JEDE
 *    Aufgabe falsch - der Filter „Meine" ist dann leer, nicht voll.
 * 3. Sortiert wird `openTasks` nach `Number(b.overdue) - Number(a.overdue)` und erst danach nach
 *    Termin, wobei ein fehlender Termin als `'9999-12-31'` ganz nach hinten faellt. Die
 *    Boolean->Zahl-Wandlung ist woertlich uebernommen.
 * 4. Die Filterknoepfe zeigen die Zaehler von `openTasks`/`overdueTasks`/`myTasks` - alle drei ueber
 *    den OFFENEN Aufgaben, nie ueber der gerade gefilterten Liste.
 * 5. `dueClass` kennt drei Zustaende, `dueLabel` VIER: `overdue` -> „überfällig <Datum>",
 *    `days === 0` -> „heute fällig", sonst Datum bzw. „ohne Termin". Die Kombination „heute
 *    fällig" + Klasse `soon` entsteht daraus, dass `days === 0 <= 7` ist.
 * 6. Der Text der Unterzeile ist `orderNo || name || 'Bestellung'` UND `description || 'Keine
 *    Beschreibung'`, verbunden mit ` · ` - beide Rueckfaelle greifen unabhaengig voneinander.
 * 7. Das Ankreuzfeld ist UNKONTROLLIERT (kein `checked` im Original): es zeigt nie den Stand von
 *    `task.done`. Das faellt nicht auf, weil die Liste nur OFFENE Aufgaben zeigt - ein Haken loest
 *    sofort ein Neuzeichnen aus, nach dem die Zeile verschwunden ist. Woertlich uebernommen; in
 *    React heisst das `defaultChecked={false}` statt `checked`, sonst waere es ein kontrolliertes
 *    Feld ohne Aenderungsweg.
 * 8. Kontotyp "Nur lesen" (Neubau, kein Bestandsvorbild): das Abhaken schreibt (`PATCH` der Aufgabe) und
 *    wird vom Server fuer ein Konto mit nur `view_*`-Rechten abgelehnt. Das Ankreuzfeld bleibt sichtbar,
 *    ist dann aber gesperrt - Titel und Optik wie die Sperre im Drawer (`NUR_LESEN_TITEL`,
 *    `.feld-gesperrt`). Die Zeile selbst oeffnet die Aufgabe weiterhin (Lesen).
 */
import { fmtDate, NUM } from "../../lib/format.ts";
import type { Aufgabenzeile } from "../../lib/uebersichtAuswertung.ts";
import { darfBestellungOeffnen } from "../Drawer/Drawer.tsx";
import { NUR_LESEN_TITEL } from "../Drawer/drawerRechte.ts";
import { BestellungSplitMarke } from "../SplitMarke.tsx";
import { StockMarke } from "../StockMarke.tsx";

// `Aufgabenzeile` (eine Zeile aus `allTasks`, index.html:14597-14605) wohnt seit dem KI-Zugang (Stufe 1)
// in `lib/uebersichtAuswertung.ts`, wo die Zeilen berechnet werden.

export interface AufgabenListeProps {
  /** `filteredTasks`. */
  zeilen: readonly Aufgabenzeile[];
  /** `openTasks.length` / `overdueTasks.length` / `myTasks.length`. */
  zaehler: { open: number; overdue: number; mine: number };
  /** `effectiveTaskFilter` - `"open"` | `"overdue"` | `"mine"`. */
  filter: string;
  /** `paymentManagerView`. */
  istZahlungsansicht: boolean;
  onFilterWaehlen: (wert: string) => void;
  /** `openOverviewTask(id, taskIndex)`, index.html:14501. */
  onAufgabeOeffnen: (bestellungId: string, aufgabenIndex: number) => void;
  /** `toggleOverviewTask(id, taskIndex, checked, event)`, index.html:14507. */
  onAufgabeUmschalten: (bestellungId: string, aufgabenIndex: number, erledigt: boolean) => void;
  /** Kontotyp "Nur lesen" (`state/nurLeser.ts`): die Ankreuzfelder sind gesperrt. Ohne Angabe: nicht gesperrt. */
  nurLeser?: boolean;
}

export function AufgabenListe({
  zeilen,
  zaehler,
  filter,
  istZahlungsansicht,
  onFilterWaehlen,
  onAufgabeOeffnen,
  onAufgabeUmschalten,
  nurLeser = false,
}: AufgabenListeProps) {
  const oeffnbareZeilen = zeilen.filter(({ order }) => darfBestellungOeffnen(order));
  // index.html:14662 - `taskFilter(value, label, count)`.
  const filterKnopf = (wert: string, beschriftung: string, anzahl: number) => (
    <button
      key={wert}
      className={`ops-filter ${filter === wert ? "active" : ""}`}
      onClick={() => onFilterWaehlen(wert)}
    >
      {beschriftung}
      <b>{NUM.format(anzahl)}</b>
    </button>
  );

  return (
    <section className="ops-panel ops-task-panel">
      <div className="ops-panel-head">
        <div className="ops-panel-title">
          <h2>
            Tasks <span className="ops-count">{NUM.format(oeffnbareZeilen.length)}</span>
          </h2>
          <p>
            {istZahlungsansicht
              ? "Nur ausdrücklich angestoßene Zahlungstasks"
              : "Bestellübergreifend nach Fälligkeit priorisiert"}
          </p>
        </div>
        <div className="ops-filters">
          {istZahlungsansicht
            ? filterKnopf("open", "Zahlungen", zaehler.open)
            : [
                filterKnopf("open", "Offen", zaehler.open),
                filterKnopf("overdue", "Überfällig", zaehler.overdue),
                filterKnopf("mine", "Meine", zaehler.mine),
              ]}
        </div>
      </div>
      <div className="ops-tasks">
        {oeffnbareZeilen.length ? (
          oeffnbareZeilen.map(({ order, task, taskIndex, due, days, overdue }) => {
            const dueClass = overdue ? "overdue" : due && days <= 7 ? "soon" : "";
            const dueLabel = overdue
              ? `überfällig ${fmtDate(due)}`
              : due
                ? days === 0
                  ? "heute fällig"
                  : fmtDate(due)
                : "ohne Termin";
            return (
              <div
                key={`${order.id as string}-${taskIndex}`}
                className={`ops-task-row ${overdue ? "overdue" : ""}`}
                onClick={() => onAufgabeOeffnen(order.id as string, taskIndex)}
              >
                <input
                  className={nurLeser ? "ops-task-check feld-gesperrt" : "ops-task-check"}
                  type="checkbox"
                  aria-label="Task erledigen"
                  disabled={nurLeser}
                  title={nurLeser ? NUR_LESEN_TITEL : undefined}
                  defaultChecked={false}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => onAufgabeUmschalten(order.id as string, taskIndex, e.target.checked)}
                />
                <div className="ops-task-copy">
                  <strong>{(task.name || "Unbenannter Task") as string}</strong>
                  <span>
                    {`${(order.orderNo || order.name || "Bestellung") as string} · ${
                      (task.description || "Keine Beschreibung") as string
                    }`}
                    <StockMarke order={order} />
                    <BestellungSplitMarke order={order} />
                  </span>
                </div>
                <div className="ops-task-owner">{(task.assignee || "Nicht zugewiesen") as string}</div>
                <div className={`ops-task-due ${dueClass}`}>{dueLabel}</div>
              </div>
            );
          })
        ) : (
          <div className="ops-empty">Für diesen Filter sind keine offenen Tasks vorhanden.</div>
        )}
      </div>
    </section>
  );
}
